/**
 * 微信支付工具类
 * 用于在微信内置浏览器中直接调起微信支付
 */

import { WxOrderInfo } from "@/types/payment";
import { isWeiXin as _isWeiXin } from "./platform";

// 重新导出isWeiXin函数
export const isWeiXin = _isWeiXin;

export interface WxPayResult {
  err_msg: string;
  err_code?: string;
}

export interface WxPayConfig {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
}

/**
 * 检查是否支持微信支付
 */
export function isSupportWxPay(): boolean {
  return isWeiXin() && typeof window.WeixinJSBridge !== 'undefined';
}

/**
 * 等待微信JS-SDK准备就绪
 */
export function waitForWeixinJSBridge(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.WeixinJSBridge !== 'undefined') {
      resolve();
    } else {
      document.addEventListener('WeixinJSBridgeReady', () => {
        resolve();
      });
    }
  });
}

/**
 * 调起微信支付
 */
export async function invokeWxPay(orderInfo: WxOrderInfo): Promise<WxPayResult> {
  if (!isWeiXin()) {
    throw new Error('请在微信中打开');
  }

  // 等待微信JS-SDK准备就绪
  await waitForWeixinJSBridge();

  const payConfig: WxPayConfig = {
    appId: orderInfo.appid,
    timeStamp: orderInfo.timestamp,
    nonceStr: orderInfo.noncestr,
    package: orderInfo.packageValue || orderInfo.package,
    signType: orderInfo.signtype,
    paySign: orderInfo.paysign,
  };

  console.log('微信支付配置:', payConfig);

  return new Promise((resolve, reject) => {
    window.WeixinJSBridge.invoke(
      'getBrandWCPayRequest',
      payConfig,
      (res: WxPayResult) => {
        console.log('微信支付结果:', res);
        
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          // 支付成功
          resolve(res);
        } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
          // 用户取消支付
          reject(new Error('用户取消支付'));
        } else {
          // 支付失败
          reject(new Error(res.err_msg || '支付失败'));
        }
      }
    );
  });
}

/**
 * 处理微信支付结果
 */
export function handleWxPayResult(result: WxPayResult): {
  success: boolean;
  message: string;
} {
  switch (result.err_msg) {
    case 'get_brand_wcpay_request:ok':
      return { success: true, message: '支付成功' };
    case 'get_brand_wcpay_request:cancel':
      return { success: false, message: '用户取消支付' };
    case 'get_brand_wcpay_request:fail':
      return { success: false, message: '支付失败' };
    default:
      return { success: false, message: result.err_msg || '未知错误' };
  }
}

// 扩展Window接口以支持微信JS-SDK
declare global {
  interface Window {
    WeixinJSBridge: {
      invoke: (
        method: string,
        params: WxPayConfig,
        callback: (result: WxPayResult) => void
      ) => void;
    };
  }
} 